import axios from "axios";

const BASE_API = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

const getAuthHeaders = () => {
  const token = localStorage.getItem("eez7_client_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Get public pricing information (no auth required)
 */
export const getMembershipPricing = async (orgSlug) => {
  try {
    // The customer widget lives under /o/:orgSlug/*, so we always know
    // which org's category catalog to pull. Backend filters accordingly.
    const slug = orgSlug || localStorage.getItem('eez7_client_orgSlug') || undefined;
    const response = await axios.get(`${BASE_API}/memberships/pricing`, {
      params: slug ? { orgSlug: slug } : undefined,
    });
    return response.data.data || response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Apply for membership. Accepts FormData (with photo files) or a plain object.
 * Axios sets the multipart boundary automatically when given FormData.
 */
export const applyForMembership = async (applicationData) => {
  try {
    const response = await axios.post(
      `${BASE_API}/memberships/apply`,
      applicationData,
      { headers: getAuthHeaders() }
    );
    return response.data.data || response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get user's membership status
 */
export const getMyMembershipStatus = async () => {
  try {
    const response = await axios.get(`${BASE_API}/memberships/my-status`, {
      headers: getAuthHeaders(),
    });
    return response.data.data || response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get membership details
 */
export const getMembershipDetails = async (id) => {
  try {
    const response = await axios.get(`${BASE_API}/memberships/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data.data || response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Initialize payment for approved membership
 */
export const initializeMembershipPayment = async (id) => {
  try {
    const response = await axios.get(`${BASE_API}/memberships/${id}/payment`, {
      headers: getAuthHeaders(),
    });
    return response.data.data || response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Check payment status
 */
export const getPaymentStatus = async (purchaseId) => {
  try {
    const response = await axios.get(`${BASE_API}/memberships/payment/${purchaseId}/status`, {
      headers: getAuthHeaders(),
    });
    return response.data.data || response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Download wallet card (PDF)
 */
export const downloadWalletCard = async (membershipId) => {
  try {
    const response = await axios.get(
      `${BASE_API}/memberships/${membershipId}/wallet-card`,
      {
        headers: getAuthHeaders(),
        responseType: "blob",
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Download QR code (PNG)
 */
export const downloadQRCode = async (membershipId) => {
  try {
    const response = await axios.get(
      `${BASE_API}/memberships/${membershipId}/qr-code`,
      {
        headers: getAuthHeaders(),
        responseType: "blob",
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Download Apple Wallet pass (.pkpass)
 */
export const downloadAppleWalletPass = async (membershipId) => {
  try {
    const response = await axios.get(
      `${BASE_API}/memberships/${membershipId}/apple-wallet`,
      {
        headers: getAuthHeaders(),
        responseType: "blob",
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all payment transactions with filters
 * @param {Object} filters - Optional filters
 * @param {number} filters.status - Payment status (-1: error, 0: pending, 1: approved, 3: captured)
 * @param {string} filters.startDate - Start date (YYYY-MM-DD)
 * @param {string} filters.endDate - End date (YYYY-MM-DD)
 * @param {string} filters.search - Search term (name, email, phone, order ID, transaction ID)
 * @param {number} filters.limit - Number of results per page
 * @param {number} filters.offset - Offset for pagination
 */
export const getAllPaymentTransactions = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.status !== undefined && filters.status !== null && filters.status !== '') {
      params.append('status', filters.status);
    }
    if (filters.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.limit) {
      params.append('limit', filters.limit);
    }
    if (filters.offset !== undefined) {
      params.append('offset', filters.offset);
    }

    const response = await axios.get(
      `${BASE_API}/memberships/payments/all?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    return response.data.data || response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * PUBLIC — fetch info for the guest-facing membership payment landing.
 * Returns { valid, reason?, data? } — the landing page renders different
 * states based on `reason` (already_paid, link_expired, no_link, etc.).
 */
export const getMembershipPayInfo = async (purchaseId) => {
  const response = await axios.get(`${BASE_API}/memberships/pay/${purchaseId}/info`);
  return response.data;
};

/**
 * PUBLIC — regenerate the Paymob payment link for a purchase whose
 * link has expired. Called from the "Generate new link" button.
 */
export const regenerateMembershipPayLink = async (purchaseId) => {
  const response = await axios.post(`${BASE_API}/memberships/pay/${purchaseId}/regenerate`);
  return response.data;
};
